import { Star } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createRecurring,
  getCategories,
  getExpenses,
  getRecurring,
  type Category,
  type Expense,
  type RecurringTemplate,
} from '../api';
import { CategoryIcon } from '../categoryIcons';
import { buildMonthGroups, type MonthGroup } from '../utils/analysis';
import { defaultAnalysisPeriod, rangeBoundsFromMonthInputs } from '../utils/dates';
import { eur } from '../utils/money';

const PERIOD_KEY = 'spese-analysis-period';
const CATEGORIES_KEY = 'spese-analysis-categories';
const EXPENSE_LIMIT = 10000;

function readStoredPeriod(): { startMonth: string; endMonth: string } | null {
  try {
    const s = sessionStorage.getItem(PERIOD_KEY);
    if (!s) return null;
    const j = JSON.parse(s) as { startMonth?: string; endMonth?: string };
    if (j.startMonth && j.endMonth) return { startMonth: j.startMonth, endMonth: j.endMonth };
  } catch {
    /* ignore */
  }
  return null;
}

function persistPeriod(startMonth: string, endMonth: string) {
  try {
    sessionStorage.setItem(PERIOD_KEY, JSON.stringify({ startMonth, endMonth }));
  } catch {
    /* ignore */
  }
}

function readStoredCategoryIds(): string[] | null {
  try {
    const s = sessionStorage.getItem(CATEGORIES_KEY);
    if (!s) return null;
    const j = JSON.parse(s) as unknown;
    if (Array.isArray(j) && j.every((x) => typeof x === 'string')) return j;
  } catch {
    /* ignore */
  }
  return null;
}

function persistCategoryIds(ids: string[]) {
  try {
    sessionStorage.setItem(CATEGORIES_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export default function AnalysisPage() {
  const defaults = defaultAnalysisPeriod();
  const storedPeriod = readStoredPeriod();
  const [startMonth, setStartMonth] = useState(storedPeriod?.startMonth ?? defaults.startMonth);
  const [endMonth, setEndMonth] = useState(storedPeriod?.endMonth ?? defaults.endMonth);

  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(readStoredCategoryIds() ?? []));

  const [analyzing, setAnalyzing] = useState(false);
  const [err, setErr] = useState('');
  const [periodErr, setPeriodErr] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [monthGroups, setMonthGroups] = useState<MonthGroup[]>([]);
  const [recurringTemplates, setRecurringTemplates] = useState<RecurringTemplate[]>([]);

  const [promoteTarget, setPromoteTarget] = useState<Expense | null>(null);
  const [promoteName, setPromoteName] = useState('');
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteErr, setPromoteErr] = useState('');
  const [promoteOk, setPromoteOk] = useState('');

  const outCategories = useMemo(
    () =>
      categories
        .filter((c) => c.type === 'out')
        .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [categories]
  );

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categories) m.set(c.categoryId, c);
    return m;
  }, [categories]);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    Promise.all([getCategories(), getRecurring()])
      .then(([catRes, recRes]) => {
        if (!cancelled) {
          setCategories(catRes.categories);
          setRecurringTemplates(recRes.templates);
        }
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Errore caricamento categorie');
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    persistPeriod(startMonth, endMonth);
  }, [startMonth, endMonth]);

  useEffect(() => {
    persistCategoryIds([...selectedIds]);
  }, [selectedIds]);

  const selectedCount = selectedIds.size;
  const canAnalyze = selectedCount > 0 && !analyzing && !categoriesLoading;

  function toggleCategory(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllOut() {
    setSelectedIds(new Set(outCategories.map((c) => c.categoryId)));
  }

  function selectNone() {
    setSelectedIds(new Set());
  }

  const runAnalysis = useCallback(async () => {
    setPeriodErr('');
    setErr('');
    const bounds = rangeBoundsFromMonthInputs(startMonth, endMonth);
    if (!bounds) {
      setPeriodErr('Il mese fine deve essere uguale o successivo al mese inizio.');
      return;
    }
    if (selectedIds.size === 0) return;

    setAnalyzing(true);
    setHasRun(false);
    try {
      const { expenses } = await getExpenses({
        dateFrom: bounds.dateFrom,
        dateTo: bounds.dateTo,
        limit: String(EXPENSE_LIMIT),
      });
      setTruncated(expenses.length >= EXPENSE_LIMIT);
      setMonthGroups(buildMonthGroups(expenses, categoryById, selectedIds));
      setHasRun(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore caricamento spese');
      setMonthGroups([]);
    } finally {
      setAnalyzing(false);
    }
  }, [startMonth, endMonth, selectedIds, categoryById]);

  const grandTotal = useMemo(
    () => monthGroups.reduce((sum, g) => sum + g.total, 0),
    [monthGroups]
  );

  const promoteDuplicate = useMemo(() => {
    if (!promoteTarget || !promoteName.trim()) return null;
    const key = promoteName.trim().toLowerCase();
    return (
      recurringTemplates.find(
        (t) =>
          t.name.trim().toLowerCase() === key && t.categoryId === promoteTarget.categoryId
      ) ?? null
    );
  }, [promoteTarget, promoteName, recurringTemplates]);

  function openPromoteModal(ex: Expense) {
    const catName = categoryById.get(ex.categoryId)?.name ?? 'Movimento';
    setPromoteOk('');
    setPromoteErr('');
    setPromoteTarget(ex);
    setPromoteName((ex.note ?? '').trim() || catName);
  }

  function closePromoteModal() {
    if (promoteBusy) return;
    setPromoteTarget(null);
    setPromoteName('');
    setPromoteErr('');
    setPromoteOk('');
  }

  async function onConfirmPromote() {
    if (!promoteTarget) return;
    const name = promoteName.trim();
    if (!name) {
      setPromoteErr('Inserisci un nome per l’anagrafica.');
      return;
    }
    if (promoteDuplicate) {
      setPromoteErr('Esiste già un movimento ricorrente con questo nome e categoria.');
      return;
    }
    const amount = Number(promoteTarget.amount) || 0;
    if (amount <= 0) {
      setPromoteErr('Importo non valido.');
      return;
    }

    setPromoteBusy(true);
    setPromoteErr('');
    setPromoteOk('');
    try {
      const created = await createRecurring({
        name,
        categoryId: promoteTarget.categoryId,
        defaultAmount: amount,
        active: true,
      });
      setRecurringTemplates((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'it'))
      );
      setPromoteOk('Aggiunto all’anagrafica dei movimenti ricorrenti.');
      setTimeout(() => {
        setPromoteTarget(null);
        setPromoteName('');
        setPromoteOk('');
      }, 900);
    } catch (e) {
      setPromoteErr(e instanceof Error ? e.message : 'Operazione non riuscita');
    } finally {
      setPromoteBusy(false);
    }
  }

  return (
    <div className="analysis">
      <h2 className="analysis-title">Analisi spese</h2>
      <p className="analysis-lead home-muted">
        Scegli le categorie di uscita e il periodo, poi visualizza totali e movimenti per mese.
      </p>

      <section className="analysis-section" aria-labelledby="analysis-period-heading">
        <h3 id="analysis-period-heading" className="analysis-section-title">
          Periodo
        </h3>
        <div className="analysis-period-row">
          <div className="field flex-1">
            <label htmlFor="analysis-start">Da</label>
            <input
              id="analysis-start"
              type="month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              disabled={analyzing}
            />
          </div>
          <div className="field flex-1">
            <label htmlFor="analysis-end">A</label>
            <input
              id="analysis-end"
              type="month"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              disabled={analyzing}
            />
          </div>
        </div>
        {periodErr && <p className="home-err">{periodErr}</p>}
      </section>

      <section className="analysis-section" aria-labelledby="analysis-categories-heading">
        <div className="analysis-section-head">
          <h3 id="analysis-categories-heading" className="analysis-section-title">
            Categorie di spesa
          </h3>
          <div className="analysis-cat-actions">
            <button type="button" className="btn-link" onClick={selectAllOut} disabled={categoriesLoading}>
              Tutte
            </button>
            <span className="analysis-cat-actions-sep" aria-hidden>
              ·
            </span>
            <button type="button" className="btn-link" onClick={selectNone} disabled={categoriesLoading}>
              Nessuna
            </button>
          </div>
        </div>

        {categoriesLoading ? (
          <p className="home-muted">Caricamento categorie…</p>
        ) : outCategories.length === 0 ? (
          <p className="home-muted">Nessuna categoria di uscita disponibile.</p>
        ) : (
          <ul className="analysis-categories">
            {outCategories.map((c) => {
              const checked = selectedIds.has(c.categoryId);
              return (
                <li key={c.categoryId}>
                  <label className={`analysis-cat-option ${checked ? 'analysis-cat-option--on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(c.categoryId)}
                      disabled={analyzing}
                    />
                    <CategoryIcon iconId={c.icon} size={22} className="analysis-cat-icon" />
                    <span className="analysis-cat-name">{c.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="analysis-run-row">
        <button
          type="button"
          className="btn-primary"
          onClick={() => void runAnalysis()}
          disabled={!canAnalyze}
        >
          {analyzing ? 'Analisi in corso…' : 'Analizza'}
        </button>
        {selectedCount === 0 && !categoriesLoading && (
          <p className="analysis-hint home-muted">Seleziona almeno una categoria.</p>
        )}
      </div>

      {err && <p className="home-err">{err}</p>}
      {truncated && (
        <p className="analysis-warn" role="status">
          Raggiunto il limite di {EXPENSE_LIMIT} movimenti: l’analisi potrebbe essere incompleta. Restringi il
          periodo.
        </p>
      )}

      {hasRun && !analyzing && (
        <section className="analysis-results" aria-labelledby="analysis-results-heading">
          <h3 id="analysis-results-heading" className="analysis-section-title">
            Risultati
            {monthGroups.length > 0 && (
              <span className="analysis-grand-total"> · Totale {eur(grandTotal)}</span>
            )}
          </h3>

          {monthGroups.length === 0 ? (
            <p className="home-muted">Nessuna spesa nelle categorie e nel periodo selezionati.</p>
          ) : (
            monthGroups.map((group) => (
              <article key={group.key} className="analysis-month">
                <header className="analysis-month-header">
                  <h4 className="analysis-month-title">{group.label}</h4>
                  <span className="analysis-month-total">{eur(group.total)}</span>
                </header>
                <ul className="expense-list">
                  {group.items.map((ex) => {
                    const cat = categoryById.get(ex.categoryId);
                    const catName = cat?.name ?? ex.categoryId;
                    const isRecurring = Boolean(ex.recurringId);
                    const alreadyInCatalog =
                      !isRecurring &&
                      recurringTemplates.some(
                        (t) =>
                          t.categoryId === ex.categoryId &&
                          t.name.trim().toLowerCase() ===
                            ((ex.note ?? '').trim() || catName).toLowerCase()
                      );
                    return (
                      <li key={ex.expenseId} className="expense-row">
                        <span className="expense-cat-icon" title={catName}>
                          <CategoryIcon
                            iconId={cat?.icon}
                            size={22}
                            className="expense-cat-svg"
                          />
                        </span>
                        <div className="expense-main">
                          <span className="expense-date">
                            {ex.date}
                            {ex.time ? ` · ${ex.time}` : ''}
                            {' · '}
                            {catName}
                          </span>
                          {ex.note ? <span className="expense-note">{ex.note}</span> : null}
                        </div>
                        <div className="expense-end">
                          <div className="expense-amount out">
                            −{eur(Math.abs(Number(ex.amount) || 0))}
                          </div>
                          {!isRecurring && (
                            <div className="expense-actions">
                              <button
                                type="button"
                                className={`icon-btn icon-btn--recurring-promote${alreadyInCatalog ? ' icon-btn--muted-done' : ''}`}
                                onClick={() => openPromoteModal(ex)}
                                disabled={alreadyInCatalog}
                                aria-label={
                                  alreadyInCatalog
                                    ? 'Già presente nei movimenti ricorrenti'
                                    : `Aggiungi «${(ex.note ?? '').trim() || catName}» ai movimenti ricorrenti`
                                }
                                title={
                                  alreadyInCatalog
                                    ? 'Già in anagrafica'
                                    : 'Aggiungi ai movimenti ricorrenti'
                                }
                              >
                                <Star
                                  size={17}
                                  strokeWidth={2}
                                  aria-hidden
                                  fill={alreadyInCatalog ? 'currentColor' : 'none'}
                                />
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))
          )}
        </section>
      )}

      {promoteTarget && (
        <div
          className="modal-backdrop modal-backdrop--confirm"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget && !promoteBusy) closePromoteModal();
          }}
        >
          <div
            className="modal-panel modal-panel--confirm"
            role="dialog"
            aria-labelledby="promote-recurring-title"
          >
            <div className="confirm-promote-visual" aria-hidden>
              <Star size={28} strokeWidth={1.75} />
            </div>
            <h3 id="promote-recurring-title" className="modal-title modal-title--center">
              Aggiungere ai movimenti ricorrenti?
            </h3>
            <p className="confirm-delete-lead">
              Verrà creata una voce in anagrafica con importo predefinito. Potrai inserirla ogni mese dalla
              stella nella home.
            </p>
            <div className="confirm-delete-card">
              <div className="field field--compact">
                <label htmlFor="promote-recurring-name">Nome in anagrafica</label>
                <input
                  id="promote-recurring-name"
                  type="text"
                  value={promoteName}
                  onChange={(e) => setPromoteName(e.target.value)}
                  disabled={promoteBusy}
                  maxLength={200}
                  required
                />
              </div>
              <p className="confirm-delete-meta">
                Categoria: {categoryById.get(promoteTarget.categoryId)?.name ?? promoteTarget.categoryId}
              </p>
              <p className="confirm-delete-amount">
                Importo predefinito: −{eur(Math.abs(Number(promoteTarget.amount) || 0))}
              </p>
              {promoteTarget.note ? (
                <p className="confirm-delete-note">Nota originale: «{promoteTarget.note}»</p>
              ) : null}
            </div>
            {promoteDuplicate && (
              <p className="field-err confirm-delete-err">
                Esiste già «{promoteDuplicate.name}» con la stessa categoria.
              </p>
            )}
            {promoteErr && <p className="field-err confirm-delete-err">{promoteErr}</p>}
            {promoteOk && <p className="field-ok confirm-delete-err">{promoteOk}</p>}
            <div className="modal-actions modal-actions--stack-sm">
              <button
                type="button"
                className="btn-primary btn-primary-inline"
                onClick={() => void onConfirmPromote()}
                disabled={promoteBusy || Boolean(promoteDuplicate)}
              >
                {promoteBusy ? '…' : 'Aggiungi all’anagrafica'}
              </button>
              <button
                type="button"
                className="btn-secondary btn-secondary-ghost"
                onClick={closePromoteModal}
                disabled={promoteBusy}
              >
                Annulla
              </button>
            </div>
            <p className="confirm-promote-footer">
              <Link to="/ricorrenti">Vai all’anagrafica ricorrenti</Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
